import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, FileText, BadgeCheck, Save, ExternalLink, FileCheck2, Clock, Inbox } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { AdminTabs } from "@/components/AdminTabs";
import { SuspensionControl } from "@/components/SuspensionControl";

const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="label-soft">{label}</label>
    <input data-testid={testid} type={type} className="input-soft" value={value ?? ""} onChange={onChange} />
  </div>
);

export default function AdminCollectorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/collectors/${id}`);
      setC(data);
      const lr = await api.get(`/admin/collectors/${id}/leads`).catch(() => null);
      if (lr?.data) setLeads(lr.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, [id]);

  if (!c) return <div className="container-x py-20 text-[#5C6B64]">Loading…</div>;

  const f = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const fa = (k) => (e) => setC({ ...c, account: { ...c.account, [k]: e.target.value } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/collectors/${id}`, {
        business_name: c.business_name, ein: c.ein, first_name: c.first_name, last_name: c.last_name,
        bio: c.bio, phone: c.phone, email: c.email, service_type: c.service_type,
        address: c.address, city: c.city, state: c.state, zip_code: c.zip_code,
        hourly_rate: parseFloat(c.hourly_rate) || 0,
        verified: !!c.verified, subscription_active: !!c.subscription_active,
      });
      if (c.account?.id) {
        await api.put(`/admin/users/${c.account.id}`, {
          name: c.account.name || undefined,
          email: c.account.email || undefined,
          phone: c.account.phone || undefined,
        });
      }
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const certs = c.certifications || [];

  const verifyCert = async (file_path, status) => {
    try {
      const { data } = await api.patch(`/admin/collectors/${id}/certifications`, { file_path, status });
      setC((prev) => ({ ...prev, certifications: data.certifications }));
      toast.success(`Document marked ${status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="container-x py-12" data-testid="admin-collector-detail">
      <AdminTabs />
      <button onClick={() => navigate("/admin/collectors")} className="text-sm text-[#5C6B64] hover:text-[#1E3F33] inline-flex items-center gap-1.5 mb-4" data-testid="admin-collector-back">
        <ArrowLeft className="w-4 h-4" /> Back to collectors
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold">{c.business_name || "Collector"}</h1>
        <button onClick={save} disabled={saving} data-testid="admin-collector-save" className="btn-primary inline-flex items-center gap-2">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="grid lg:grid-cols-12 gap-6 mt-8">
        <div className="lg:col-span-8 space-y-6">
          <div className="card-soft">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-4">Account</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Account name" value={c.account?.name} onChange={fa("name")} testid="admin-acct-name" />
              <Field label="Account email" type="email" value={c.account?.email} onChange={fa("email")} testid="admin-acct-email" />
              <Field label="Account phone" value={c.account?.phone} onChange={fa("phone")} testid="admin-acct-phone" />
              <div><label className="label-soft">Username</label><input className="input-soft bg-[#F0EFEA]" value={c.account?.username || ""} disabled /></div>
            </div>
          </div>

          <div className="card-soft">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-4">Business profile</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Field label="Business name" value={c.business_name} onChange={f("business_name")} testid="admin-col-business" /></div>
              <Field label="EIN" value={c.ein} onChange={f("ein")} testid="admin-col-ein" />
              <Field label="Profile email" value={c.email} onChange={f("email")} testid="admin-col-email" />
              <Field label="First name" value={c.first_name} onChange={f("first_name")} testid="admin-col-first" />
              <Field label="Last name" value={c.last_name} onChange={f("last_name")} testid="admin-col-last" />
              <Field label="Phone" value={c.phone} onChange={f("phone")} testid="admin-col-phone" />
              <div>
                <label className="label-soft">Service type</label>
                <select className="input-soft" value={c.service_type || "mobile"} onChange={f("service_type")} data-testid="admin-col-service-type">
                  <option value="mobile">Mobile</option>
                  <option value="stationary">Stationary</option>
                  <option value="both">Both</option>
                </select>
              </div>
              <div className="sm:col-span-2"><Field label="Street address" value={c.address} onChange={f("address")} testid="admin-col-address" /></div>
              <Field label="City" value={c.city} onChange={f("city")} testid="admin-col-city" />
              <Field label="State" value={c.state} onChange={f("state")} testid="admin-col-state" />
              <Field label="Zip code" value={c.zip_code} onChange={f("zip_code")} testid="admin-col-zip" />
              <Field label="Hourly rate" type="number" value={c.hourly_rate} onChange={f("hourly_rate")} testid="admin-col-rate" />
              <div className="sm:col-span-2"><label className="label-soft">Bio</label><textarea className="input-soft min-h-[80px]" value={c.bio || ""} onChange={f("bio")} data-testid="admin-col-bio" /></div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <div className="card-soft">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-4">Status</h2>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-[#1E3F33] inline-flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[#2D6A4F]" /> Verified</span>
              <Switch data-testid="admin-col-verified" checked={!!c.verified} onCheckedChange={(v) => setC({ ...c, verified: v })} />
            </div>
            <div className="flex items-center justify-between py-2 border-t border-[#1E3F33]/8">
              <span className="text-sm text-[#1E3F33]">Subscription active</span>
              <Switch data-testid="admin-col-subscription" checked={!!c.subscription_active} onCheckedChange={(v) => setC({ ...c, subscription_active: v })} />
            </div>
            {c.subscription_until && <p className="text-xs text-[#8A9A86] mt-2">Renews/expires: {fmt(c.subscription_until)}</p>}
          </div>

          <SuspensionControl account={c.account} onChange={load} />

          <div className="card-soft" data-testid="admin-col-certifications">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-[#2D6A4F]" /> Uploaded documents ({certs.length})</h2>
            {certs.length === 0 ? (
              <p className="text-sm text-[#8A9A86]">No documents uploaded.</p>
            ) : (
              <ul className="space-y-2">
                {certs.map((d, i) => {
                  const st = d.verification_status || "pending";
                  return (
                  <li key={i} className="border border-[#1E3F33]/10 rounded-lg p-2.5 space-y-2" data-testid={`admin-col-cert-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm text-[#1E3F33] truncate">{d.original_name}</div>
                        <div className="text-[11px] text-[#8A9A86]">{fmt(d.uploaded_at)}</div>
                      </div>
                      <a href={`${API}/files/${d.file_path}`} target="_blank" rel="noreferrer" data-testid={`admin-col-cert-view-${i}`} className="text-[#CC5A3A] hover:underline text-xs inline-flex items-center gap-1 shrink-0">
                        <ExternalLink className="w-3.5 h-3.5" /> View
                      </a>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span data-testid={`admin-col-cert-status-${i}`} className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${st === "verified" ? "bg-[#2D6A4F]/12 text-[#2D6A4F]" : st === "rejected" ? "bg-[#CC5A3A]/12 text-[#CC5A3A]" : "bg-[#8A9A86]/15 text-[#5C6B64]"}`}>
                        {st === "verified" ? "Verified" : st === "rejected" ? "Rejected" : "Unverified"}
                      </span>
                      <button type="button" onClick={() => verifyCert(d.file_path, "verified")} data-testid={`admin-col-cert-verify-${i}`} className="text-xs px-2 py-1 rounded-lg border border-[#2D6A4F]/30 text-[#2D6A4F] hover:bg-[#2D6A4F]/10">Verify</button>
                      <button type="button" onClick={() => verifyCert(d.file_path, "rejected")} data-testid={`admin-col-cert-reject-${i}`} className="text-xs px-2 py-1 rounded-lg border border-[#CC5A3A]/30 text-[#CC5A3A] hover:bg-[#CC5A3A]/10">Reject</button>
                    </div>
                    {(d.matched_keywords || []).length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap" data-testid={`admin-col-cert-keywords-${i}`}>
                        <span className="text-[11px] text-[#8A9A86]">Recognized:</span>
                        {d.matched_keywords.slice(0, 6).map((kw, j) => (
                          <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-[#2D6A4F]/8 text-[#2D6A4F]">{kw}</span>
                        ))}
                      </div>
                    )}
                    {d.verified_by && <div className="text-[11px] text-[#8A9A86]">{st} by {d.verified_by}{d.verified_at ? ` · ${fmt(d.verified_at)}` : ""}</div>}
                  </li>
                )})}
              </ul>
            )}
          </div>

          <div className="card-soft" data-testid="admin-col-agreements">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-3 flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-[#2D6A4F]" /> Agreements</h2>
            {(c.agreements || []).length === 0 ? (
              <p className="text-sm text-[#8A9A86]">No agreement records on file.</p>
            ) : (
              <ul className="space-y-2">
                {(c.agreements || []).map((a, i) => (
                  <li key={i} className="text-sm border border-[#1E3F33]/10 rounded-lg p-2.5">
                    <div className="text-[#1E3F33] font-medium">{a.title}</div>
                    <div className="text-[11px] text-[#8A9A86] mt-0.5">v{a.version} · {fmt(a.accepted_at)}{a.ip ? ` · IP ${a.ip}` : ""}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="card-soft" data-testid="admin-col-lead-activity">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-1 flex items-center gap-2"><Inbox className="w-4 h-4 text-[#2D6A4F]" /> Lead activity ({leads.length})</h2>
            <p className="text-xs text-[#8A9A86] mb-3">Every lead sent to this collector and what happened — use this to resolve "I never got the lead" questions.</p>
            {leads.length === 0 ? (
              <p className="text-sm text-[#8A9A86]">No leads have been sent to this collector yet.</p>
            ) : (
              <ul className="space-y-3">
                {leads.map((l) => (
                  <li key={l.id} className="border border-[#1E3F33]/10 rounded-lg p-3" data-testid={`admin-lead-${l.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium text-[#1E3F33]">{l.customer_name || "Customer"}</div>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1E3F33]/8 text-[#1E3F33] whitespace-nowrap">{l.status}</span>
                    </div>
                    <div className="text-[11px] text-[#8A9A86] mt-0.5">{(l.services || []).join(", ")} · {(l.zip_codes || []).join(", ")}</div>
                    <ol className="mt-2 border-l border-[#1E3F33]/10 pl-3 space-y-1">
                      {(l.timeline || []).map((ev, i) => (
                        <li key={i} className="text-[11px] text-[#5C6B64] flex items-center gap-1.5">
                          <Clock className="w-2.5 h-2.5 text-[#8A9A86]" />
                          <span className="text-[#1E3F33]">{ev.label}</span>
                          <span className="text-[#8A9A86]">· {fmt(ev.at)}</span>
                        </li>
                      ))}
                    </ol>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
